.sidebar {
  flex: 1 1 0%;
  display: flex;
  position: sticky; /* don't scroll with the page */
  top: 0;
  z-index: 1; /* above main content */
  max-width: 28em;
  height: 100vh;
  padding: 1rem 1.25rem 0 1.75rem;
  overflow-x: hidden;
  overflow-y: scroll;
  scrollbar-width: thin;
  color: #f8fbef;
  transition: max-width 0.2s ease-out;
  accent-color: var(--color-five);
}

.sidebar:not(:hover) {
  /* Hide scrollbar until sidebar is hovered (FF on Windows) */
  scrollbar-color: transparent transparent;
}

.sidebar[data-collapsed] {
  max-width: 4.5em;
  overflow: visible;
}

.sidebarInner {
  flex: 1 1 0%;
  min-width: 0;
  display: flex;
  flex-direction: column;
  width: 20vw;
}

.sidebar[data-collapsed] .sidebarInner {
  align-items: flex-start;
}

.sidebarInner [data-reveal] {
  animation: 0.3s reveal ease-out backwards;
}

@keyframes reveal {
  from {
    opacity: 0;
  }
}

.logo {
  flex: none;
  margin: 0;
  font-family: var(--font-logo);
  font-size: clamp(2.25em, 4vw, 3em);
  font-weight: normal;
  letter-spacing: 0.05em;
}

.logoLink {
  margin: -0.25rem -0.5rem;
  padding: 0.25rem 0.5rem;
  border-radius: 1.5rem; /* for focus outline */
  color: inherit;
  text-decoration: none;
}

.logoLink[aria-current] {
  pointer-events: none; /* remove interactivity when already on homepage to avoid confusion */
}

.logo5 {
  color: var(--color-five);
}

.sidebar[data-collapsed] .logo {
  display: none;
}

.nav {
  flex: 1 1 0%;
  margin-top: 2rem;
}

.sidebar[data-collapsed] .nav {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  margin-top: 0;
}

.navList {
  margin: 0;
  padding-left: 0;
  list-style-type: none;
}

.navListItem {
  position: relative;
}

.navItem {
  display: flex;
  align-items: center;
  margin: 0.25rem -1rem;
  padding: calc(0.5rem + 1px) 1rem 0.5rem; /* tweak horizontal centering */
  color: inherit;
  border-radius: 1.5rem;
  text-decoration: none;
}

.mainNavItem {
  composes: navItem;
}

.navBtn {
  composes: mainNavItem;
}

.sidebar[data-collapsed] .mainNavItem {
  font-size: 1.375em;
  margin-top: 1rem;
}

:where(.sidebar[data-collapsed]) .navItem[data-primary] {
  background-color: hsl(240, 6%, 45%);
}

.navItem[aria-current] {
  background-color: hsl(240, 6%, 15%);
  font-weight: 600;
}

.navItem:not([aria-current]):hover,
.navListItem:hover > .navItem:not([aria-current]) {
  background-color: hsl(240, 6%, 25%);
}

.icon {
  flex: none;
  margin-right: 0.5rem;
}

.sidebar[data-collapsed] .mainNavItem > .icon {
  margin-right: 0;
}

.label {
  flex: 1 1 0%;
  margin-right: 0.5rem;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
}

.sidebar[data-collapsed] .mainNavItem > .label {
  display: none;
}

.flyoutWrapper {
  position: relative;
  z-index: 0; /* stacking context */
}

.flyoutBtn {
  composes: navBtn;
  position: relative;
  z-index: 2; /* for cursor to remain `pointer` while moving to flyout */
}

.flyout {
  position: absolute;
  top: 0.5rem;
  left: 100%;
  z-index: 1;
  padding-left: 2.25rem;
  opacity: 0;
  transition: opacity 0.15s 0.75s ease-in-out;
  /* Flyout has to be in flow for opacity transition to work, so we need to prevent
     hovering it by default. This property is set to `auto` in JS when fade-in
     transition starts, and back to `none` when fade-out transition ends. */
  pointer-events: none;
}

.flyoutWrapper:hover > .flyout,
.flyoutBtn:focus + .flyout,
.flyout:focus-within {
  opacity: 1;
  transition-delay: 0s; /* show right away (but hide after delay) */
}

.flyoutInner {
  min-width: 18em;
  max-width: 40em;
  padding: 0.25rem 1.5rem 0.5rem;
  background-color: var(--color-gray);
  border-radius: 1rem;
  box-shadow: 0px 2px 8px rgba(90, 90, 102, 0.8);
}

.heading {
  font-size: 0.875em;
  font-weight: 600;
  white-space: nowrap;
  color: var(--color-five);
}

.heading:not(:first-child) {
  margin-top: 3rem; /* when inside flyout */
}

.heading:not(:first-child)::before {
  content: '';
  display: block;
  border-top: 1px solid rgba(248, 251, 239, 0.75);
  margin-bottom: 1rem;
}

.actionBtnGroup {
  position: absolute;
  top: 1px;
  bottom: 0;
  right: 0;
  display: flex;
  align-items: center;
}

.actionBtn {
  flex: none;
  display: flex;
  align-items: center;
  margin: -0.5rem 0;
  padding: 0.375rem;
  border-radius: 50%;
  opacity: 0;
  transition: opacity 0.1s ease-out;
}

.downloadBtn {
  composes: actionBtn;
  color: inherit;
}

.removeBtn {
  composes: actionBtn;
}

.actionBtnGroup:hover > .actionBtn,
.navListItem:hover > .actionBtnGroup > .actionBtn,
.navListItem:focus-within > .actionBtnGroup > .actionBtn {
  opacity: 0.7;
}

.actionBtn:hover {
  background-color: rgba(255, 255, 255, 0.2);
  opacity: 1 !important;
}

.navListItem:hover > .navItem,
.navListItem:focus-within > .navItem,
.flyout .navItem {
  padding-right: 4.125em;
}

.hint {
  font-style: italic;
}

.footer {
  display: flex;
  align-items: center;
  margin-top: 1rem;
  padding-bottom: 1rem;
}

.credits {
  margin: 0 0 0 1.5rem;
  font-size: 0.75em;
  white-space: nowrap; /* prevent wrapping while expanding */
}

.sidebar[data-collapsed] .credits {
  display: none;
}

.credits > a {
  color: powderblue;
}

.credits > a:hover,
.credits > a:focus-visible {
  text-decoration: none;
}
